import type React from 'react'
import styled from 'styled-components'

import { isFieldHighlighted } from '@helpers'

import { Input } from '../InputElements/Input'
import { InputLabel } from '../InputElements/InputLabel'
import type { InputProps, TextFieldProps } from '../InputElements/types'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'
import type { ReactHookFormTextFieldProps } from './ReactHookFormTextField'

const ExtraTextWrapper = styled.div`
  & > * {
    margin-top: 3px;
    font-size: 12px;

    & > button {
      height: auto;
      font-size: 12px;
      line-height: 16px;
      color: ${({ theme }) => theme.colors.field.value};
      background-color: transparent;
      border: none;
      padding: 0;
      letter-spacing: -0.4px;
      text-align: left;
      font-style: italic;
    }
  }
`

const StyledFormControl = styled(FormControl)`
  &:has(${ExtraTextWrapper}) ${FormHelperText} {
    margin-top: 3px;
  }
`

export type TextFieldBehaviorProps = ReactHookFormTextFieldProps & {
  endAdornment?: InputProps['endAdornment']
  error: TextFieldProps['error']
  ExtraTextComponent?: React.ComponentType<{ className?: string }>
  helperText?: TextFieldProps['helperText']
}

//* integration component to display NumberFormat as TextField
export function TextFieldBehavior({
  className,
  endAdornment,
  error,
  ExtraTextComponent,
  helperText,
  highlighted,
  isLabelHighlighted = false,
  label,
  required,
  ...rest
}: TextFieldBehaviorProps) {
  return (
    <StyledFormControl className={className} error={Boolean(error)}>
      {rest.type !== 'hidden' && label && (
        <InputLabel highlighted={isLabelHighlighted} htmlFor={rest.name} error={error} required={required}>
          {label}
        </InputLabel>
      )}
      <Input
        highlighted={isFieldHighlighted(highlighted, rest.value)}
        error={error}
        endAdornment={endAdornment}
        id={rest.name}
        required={required}
        {...rest}
      />
      {ExtraTextComponent && (
        <ExtraTextWrapper>
          <ExtraTextComponent />
        </ExtraTextWrapper>
      )}
      {error && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  )
}
